import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Badge from '../Badge';
import ChevronRight from '../icons/ChevronRight';
import { pluralForm } from '../../helpers/pluralForm';
const NO_CURRENT_YEAR = 'NO_CURRENT_YEAR_CALCULATION';


const isoToDotted = (iso) => iso.split('-').reverse().join('.');

const groupByBroker = (accounts) => {
  const groups = new Map();
  accounts.forEach((a) => {
    if (!groups.has(a.dataProvider)) groups.set(a.dataProvider, []);
    groups.get(a.dataProvider).push(a);
  });
  return [...groups.entries()];
};

// One broker pill (mock .fresh-broker): details/summary opened by click, Enter or Space, a 300 px popover of accounts.
function BrokerPill({ provider, accounts, k, t, plural, accountBadge }) {
  const [open, setOpen] = useState(false);
  const stale = accounts.filter((a) => a.stale).length;
  const toggle = (e) => {
    e.preventDefault();
    setOpen((v) => !v);
  };
  return (
    <details open={open} className="relative">
      <summary
        data-testid={`broker-pill-${provider}`}
        onClick={toggle}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(e)}
        className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-full border border-border bg-surface py-1 pl-2.5 pr-2 hover:bg-surface-alt [&::-webkit-details-marker]:hidden">
        <b className="font-semibold text-text">{t(`taxPlanner.brokers.${provider}`, { defaultValue: provider })}</b>
        <span>{plural('accounts', accounts.length)}</span>
        {stale > 0 ? <Badge size="md" tone="warn">{k('staleOf', { stale, total: accounts.length })}</Badge> : <Badge size="md" tone="fresh">{k('fresh')}</Badge>}
        <span className={clsx('transition-transform', open && 'rotate-90')}>
          <ChevronRight />
        </span>
      </summary>
      {open && (
        <div className="absolute left-0 top-[calc(100%+6px)] z-20 w-[300px] max-w-[calc(100vw-32px)] rounded-xl border border-border bg-surface p-1.5 shadow-pop">
          {accounts.map((a) => (
            <div
              key={a.investingAccountId}
              data-testid={`report-account-${a.originalId}`}
              className="flex items-center justify-between gap-3 border-border px-2.5 py-2 text-xs [&+&]:border-t">
              <span>
                {k('account')} <b className="num font-semibold text-text">{a.originalId}</b>
                {a.dataAsOf && <span className="num"> · {k('asOf', { date: isoToDotted(a.dataAsOf) })}</span>}
              </span>
              {accountBadge(a)}
            </div>
          ))}
        </div>
      )}
    </details>
  );
}

// «Дані звітів» under the page title (mock .fresh-row): one pill per broker.
function ReportsData() {
  const { t, i18n } = useTranslation();
  const snapshot = useSelector((state) => state.taxPlanner.snapshot);
  const k = (key, opts) => t(`taxPlanner.scr02.reports.${key}`, opts);
  const plural = (key, count) => k(`${key}_${pluralForm(count, i18n.language)}`, { count });

  const accountBadge = (a) => {
    if (a.participation === NO_CURRENT_YEAR) return <Badge size="md" tone="warn">{k('none')}</Badge>;
    if (a.stale) return <Badge size="md" tone="warn">{plural('ago', a.dataAgeDays)}</Badge>;
    return <Badge size="md" tone="fresh">{k('fresh')}</Badge>;
  };

  return (
    <div data-testid="reports-data" className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-text-muted">
      <span>{k('label')}</span>
      {groupByBroker(snapshot?.accounts || []).map(([provider, accounts]) => (
        <BrokerPill key={provider} provider={provider} accounts={accounts} k={k} t={t} plural={plural} accountBadge={accountBadge} />
      ))}
    </div>
  );
}

export default ReportsData;
